import type {
  LotDetailResponse,
  LotListItem,
  LotLiveState,
  LotTier,
} from '@skinstep/shared';
import { parseSkinMarketName, type WeaponKind, type Wear } from '@skinstep/shared/skins';
import type { Bidder, Lot, Rarity } from '../data/mockLots';

const TIER_PALETTE: Record<LotTier, { from: string; via: string; to: string; accent: string }> = {
  A: { from: '#1a0d05', via: '#d97520', to: '#fff', accent: '#ff8c1a' },
  B: { from: '#0a0014', via: '#3a0d6e', to: '#0fc6e8', accent: '#b537ff' },
  C: { from: '#0a0000', via: '#1a0202', to: '#ff1a1a', accent: '#ff4444' },
};

const TIER_RARITY: Record<LotTier, Rarity> = {
  A: 'restricted',
  B: 'classified',
  C: 'covert',
};

const FALLBACK_KIND_AS_MOCK: Record<WeaponKind, Lot['weapon']> = {
  sniper: 'sniper',
  rifle: 'rifle',
  pistol: 'pistol',
  smg: 'smg',
  knife: 'knife',
  heavy: 'rifle',
  other: 'rifle',
};

const FALLBACK_WEAR: Wear = 'FT';
const DEFAULT_LOT_DURATION_MS = 5 * 60_000;

export function placeholderBidder(
  userId: number | null,
  firstName: string | null = null,
  username: string | null = null,
): Bidder {
  const id = userId ?? 0;
  const name = firstName?.trim() || (id ? `Player #${id}` : 'No bids yet');
  const initial = name && name !== 'No bids yet' ? name.charAt(0).toUpperCase() : '?';
  return {
    id: id ? `u${id}` : 'u?',
    firstName: name,
    username: username && username.trim() ? username : null,
    initial,
    hue: (Math.abs(id) * 53) % 360,
    bidCount: 0,
  };
}

function deriveCode(id: string, tier: LotTier): string {
  const tail =
    id
      .replace(/[^a-z0-9]/gi, '')
      .slice(-3)
      .toUpperCase() || '000';
  return `${tier}-${tail}`;
}

export function lotFromApi(item: LotListItem): Lot {
  const parsed = parseSkinMarketName(item.skin_market_name);
  const palette = TIER_PALETTE[item.tier];
  const marketPrice = item.skin_market_price_cents / 100;
  const live = item.live_state;

  const baseTimerEndsAt = live
    ? live.timer_expires_at
    : item.scheduled_start_at + DEFAULT_LOT_DURATION_MS;

  const lastBidder = placeholderBidder(
    live?.last_bidder_user_id ?? null,
    live?.last_bidder_first_name ?? null,
    live?.last_bidder_username ?? null,
  );

  return {
    id: item.id,
    code: deriveCode(item.id, item.tier),
    tier: item.tier,
    weapon: FALLBACK_KIND_AS_MOCK[parsed.weaponKind],
    weaponLabel: parsed.weapon || item.skin_market_name,
    skinName: parsed.skinName || '',
    rarity: TIER_RARITY[item.tier],
    wear: parsed.wear ?? FALLBACK_WEAR,
    float: 0,
    marketPrice,
    currentPrice: live ? live.current_price_cents / 100 : (item.final_price_cents ?? 0) / 100,
    bidsCount: live?.bids_count ?? item.final_bids_count ?? 0,
    timerEndsAt: baseTimerEndsAt,
    startsAt: item.status === 'scheduled' ? item.scheduled_start_at : undefined,
    closedAt: item.closed_at ?? undefined,
    finalPrice: item.final_price_cents != null ? item.final_price_cents / 100 : undefined,
    lastBidder,
    recentBidders: [],
    totalBidders: live?.bids_count ?? item.final_bids_count ?? 0,
    palette,
  };
}

export type FetchLotsParams = { status?: 'scheduled' | 'live' | 'closed' };
export type LotsListResponse = { lots: LotListItem[] };
export type { LotDetailResponse };

export type LotChatLink = {
  chat_id: number;
  message_thread_id: number;
  deep_link: string;
  closed_at: number | null;
};

export function applyLiveState(lot: Lot, live: LotLiveState | null): Lot {
  if (!live) return lot;
  return {
    ...lot,
    currentPrice: live.current_price_cents / 100,
    bidsCount: live.bids_count,
    timerEndsAt: live.timer_expires_at,
    lastBidder: placeholderBidder(
      live.last_bidder_user_id,
      live.last_bidder_first_name,
      live.last_bidder_username,
    ),
    totalBidders: live.bids_count,
    closedAt: live.closed_at ?? lot.closedAt,
  };
}

export function lotFromDetail(detail: LotDetailResponse): Lot {
  const listItem: LotListItem = { ...detail.lot, live_state: detail.live_state };
  return lotFromApi(listItem);
}
